import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Card, Container, Typography, TextField } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Content from '../Solicitor/Content';
import { sendNotification } from 'src/utils/sendNotification';
import ApplicationStatus from 'src/ui-component/ApplicationStatus';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';
import { getApplicationDetails } from '../common/getApplicationDetails';
import PetApplicationFeeWithFormik from 'src/pages/Permits/PetPermit/PetApplicationFeeWithFormik';
import PetSelection from './PetSelection';
import ApplicationFormLayout from '../common/ApplicationFormLayout';
import DownloadPopUp from 'src/ui-component/DownloadApplicationForm/DownloadPopUp';
import { ApplicationResponseData } from 'src/types/PetPermit/petPermit.type';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import { calculatePetLicenseFee } from 'src/components/common/calculatePetFee';
import {
  US_STATE_CODES,
  combineOwnerAddress,
  parseOwnerAddress
} from 'src/utils/usStates';
/**
 * Interface for form display data which might include string representation
 * for boolean values that are shown in form UI elements
 */
interface PetApplicationFormDisplayData {
  ApplicantName: string;
  OwnerAddress: string;
  OwnerContactNumber: string;
  OwnerEmailAddress: string;
  OwnerName: string;
  RabiesIssueDate: string;
  RabiesExpirationDate: string;
  VetClinic: string;
  VetPhoneNumber: string;
  ApplicationType: string;
  ApplicationDate?: string; // Application date (editable only for Manager/Admin)
  LateFeesWaived?: boolean; // Late fees waived flag
}

/**
 * Interface for fee details
 */
interface FeeDetails {
  applicationFee: number;
  [key: string]: number;
}

// Variable to store edit data outside component (consider moving inside component with useRef)

/**
 * EditApplication Component - Handles creating and editing pet license applications
 *
 * @returns React component for pet license application form
 */
const EditApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<typeof FormikForm | null>(null);

  // Get current permit role from Redux store
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );

  const [switchTo, setSwitchTo] = useState<string>('');
  const [clickedOn, setClickedOn] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [petDetails, setPetDetails] = useState(null);
  //let editData: ApplicationResponseData | null = null;
  const [editData, setEditData] = useState<ApplicationResponseData | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const rejectReasonRef = useRef<string>('');
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
  const approvalNoteRef = useRef<string>('');
  const pendingApproveData = useRef<any>(null); // Store form data for approval after modal
  // Get URL parameters
  const { townshipId, permit, applicationID } =
    useParams<Record<string, string>>();

  // Get socket and user data from Redux store
  const socket = useSelector((store: RootState) => store.socket.socket);
  const userData = useSelector((store: RootState) => store.user.userDetails);
  
  // Check if user is Manager or Admin (for ApplicationDate and LateFeesWaived)
  const isManagerOrAdmin = 
    currentPermitRole === 'Manager' || 
    userData?.UserRole === 'Admin' || 
    userData?.UserRole === 'Super Admin';

  // State for fee details
  const [formState, setFormState] = useState<{
    taxRate: number;
    feeDetails: FeeDetails;
  }>({
    taxRate: 0,
    feeDetails: { applicationFee: 500 }
  });

  // State for application details with default empty values
  // Using the display format for form fields
  const [applicationDetails, setApplicationDetails] =
    useState<PetApplicationFormDisplayData>({
      ApplicantName: '',
      OwnerAddress: '',
      OwnerContactNumber: '',
      OwnerEmailAddress: '',
      OwnerName: '',
      RabiesIssueDate: '',
      RabiesExpirationDate: '',
      VetClinic: '',
      VetPhoneNumber: '',
      ApplicationType: '',
      ApplicationDate: '',
      LateFeesWaived: false
    });

  /**
   * Fetches application details from the server
   * Sets the application details in the state for form initialization
   */
  const handleGetApplicationDetails = async () => {
    // Check if the application is a draft
    const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft;

    // Get application details from the server
    const response = await getApplicationDetails({
      applicationID,
      permit,
      isDraft
    });

    if (response?.status === 'success') {
      // Store the response data in the editData variable
      const data = response?.data?.data[0];
      setEditData(data);

      // Format CreateDate as ApplicationDate (YYYY-MM-DD format)
      const applicationDate = data?.CreateDate 
        ? new Date(data.CreateDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      // Update application details state with data from the server
      // Convert the boolean IsSpayedNeutered to string for form display
      setApplicationDetails({
        ApplicantName: data?.ApplicantName || '',
        OwnerAddress: data?.OwnerAddress || '',
        OwnerContactNumber: data?.OwnerContactNumber || '',
        OwnerEmailAddress: data?.OwnerEmailAddress || '',
        OwnerName: data?.OwnerName || '',
        RabiesIssueDate: data?.RabiesIssueDate || '',
        RabiesExpirationDate: data?.RabiesExpirationDate || '',
        VetClinic: data?.VetClinic || '',
        VetPhoneNumber: data?.VetPhoneNumber || '',
        ApplicationType: data?.ApplicationType || '',
        ApplicationDate: applicationDate,
        LateFeesWaived: data?.LateFeesWaived === true || data?.LateFeesWaived === 1 || data?.LateFeesWaived === 'true'
      });
    }
  };

  // Fetch application details when the component mounts or when applicationID changes
  useEffect(() => {
    if (!applicationDetails?.ApplicantName) handleGetApplicationDetails();
  }, [applicationID, permit, applicationDetails]);

  // Watch for Reject button click and open modal
  useEffect(() => {
    if (clickedOn === 'Reject') {
      setRejectModalOpen(true);
      setClickedOn(''); // Reset to prevent re-triggering
    }
  }, [clickedOn]);

  // Watch for Approve button click and open modal
  useEffect(() => {
    if (clickedOn === 'Approve') {
      // The form data is already stored in pendingApproveData.current by handleSubmit
      // Just open the modal
      setApproveModalOpen(true);
      setClickedOn(''); // Reset to prevent re-triggering
    }
  }, [clickedOn]);

  // Define form fields with their properties
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicationDate',
      fieldLabel: 'Application Date',
      fieldType: 'pastDate',
      required: true,
      disabled: !isManagerOrAdmin, // Only editable for Manager/Admin
      initialValue: applicationDetails?.ApplicationDate || new Date().toISOString().split('T')[0]
    },
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails?.ApplicantName || ''
    },
    {
      fieldName: 'OwnerName',
      fieldLabel: 'Owner Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails?.OwnerName || ''
    },
    {
      fieldName: 'OwnerContactNumber',
      fieldLabel: 'Owner Contact Number',
      fieldType: 'mobile',
      required: true,
      initialValue: applicationDetails?.OwnerContactNumber || ''
    },
    {
      fieldName: 'OwnerEmailAddress',
      fieldLabel: 'Owner Email Address',
      fieldType: 'email',
      required: false,
      initialValue: applicationDetails?.OwnerEmailAddress || ''
    },
    {
      fieldName: 'OwnerAddressLine1',
      fieldLabel: 'Owner Address',
      fieldType: 'text',
      required: true,
      initialValue: parseOwnerAddress(applicationDetails?.OwnerAddress).line1
    },
    {
      fieldName: 'OwnerCity',
      fieldLabel: 'City',
      fieldType: 'text',
      required: true,
      initialValue: parseOwnerAddress(applicationDetails?.OwnerAddress).city
    },
    {
      fieldName: 'OwnerState',
      fieldLabel: 'State',
      fieldType: 'select',
      required: true,
      options: US_STATE_CODES,
      initialValue: parseOwnerAddress(applicationDetails?.OwnerAddress).state
    },
    {
      fieldName: 'OwnerZipCode',
      fieldLabel: 'Zip Code',
      fieldType: 'text',
      required: true,
      lengthLimit: 10,
      initialValue: parseOwnerAddress(applicationDetails?.OwnerAddress).zip
    },
    {
      fieldName: 'RabiesIssueDate',
      fieldLabel: 'Rabies Issue Date',
      fieldType: 'pastDate',
      required: false,
      initialValue: applicationDetails.RabiesIssueDate || ''
    },
    {
      fieldName: 'RabiesExpirationDate',
      fieldLabel: 'Rabies Expiration Date',
      fieldType: 'futureDate',
      required: true,
      initialValue: applicationDetails.RabiesExpirationDate || ''
    },
    {
      fieldName: 'VetClinic',
      fieldLabel: 'Vet Clinic',
      fieldType: 'text',
      required: false,
      initialValue: applicationDetails.VetClinic || ''
    },
    {
      fieldName: 'VetPhoneNumber',
      fieldLabel: 'Vet Phone Number',
      fieldType: 'mobile',
      required: false,
      initialValue: applicationDetails.VetPhoneNumber || ''
    },
    // Late fees waiver checkbox - only for Manager/Admin, placed on the right side of VetPhoneNumber
    ...(isManagerOrAdmin
      ? ([
          {
            fieldName: 'LateFeesWaived',
            fieldLabel: 'Waive Late Fees',
            fieldType: 'check' as const,
            required: false,
            initialValue: applicationDetails?.LateFeesWaived ?? false
          }
        ] as Fields[])
      : [])
  ];

  const toBoolean = (value: string | boolean | number) =>
    value === 'Yes' || value === true || value === 1 || value === '1';
  const toYesNo = (value: boolean) => value ? 'Yes' : 'No';

  const getCalculatedApplicationFee = (data: any) => {
    const isSpayed = toBoolean(data?.IsSpayedNeutered);
    const baseFee = isSpayed
      ? formState.feeDetails.applicationFeeSpayed
      : formState.feeDetails.applicationNonFeeSpayed;
    const applicationDate = data?.ApplicationDate
      ? new Date(data.ApplicationDate)
      : new Date();
    return data?.LateFeesWaived
      ? baseFee || 0
      : calculatePetLicenseFee(
          baseFee || 0,
          formState.feeDetails.lateMonthlyFee || 0,
          formState.feeDetails.delinquencyFee || 0,
          applicationDate,
          isSpayed
        );
  };

  /**
   * Handles proceeding to payment after updating application
   * @param data - The form data from the UI (with IsSpayedNeutered as string)
   */
  const handleProceedToPay = async (data) => {
    const calculatedApplicationFee = getCalculatedApplicationFee(data);
    const apiData = {
      ...data,
      IsSpayedNeutered: toBoolean(data.IsSpayedNeutered),
      ApplicationDate: data.ApplicationDate || new Date().toISOString().split('T')[0],
      LateFeesWaived: data.LateFeesWaived || false,
      ApplicationFee: calculatedApplicationFee
    };

    apiData.ApplicationType = applicationDetails?.ApplicationType;

    const response = await httpRequest(
      'PATCH',
      `/pet/pet_application/update/${editData?.ApplicationID}`,
      apiData
    );

    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');

      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries({
            ...formState.feeDetails,
            applicationFee: calculatedApplicationFee
          }).filter(([, value]) => value > 0)
        ),
        ...(formState.taxRate > 0 ? { taxRate: formState.taxRate } : {})
      };

      // Prepare application details for payment
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: editData?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix: 'Pet'
        }
      };

      // Navigate to payment methods page with payment and application details
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  /**
   * Handles updating an application
   * @param data - The form data from the UI (with IsSpayedNeutered as string)
   * @param type - The type of update (empty or 'Approve')
   * @returns Promise<boolean> - Returns true if update is successful
   */
  const handleUpdateApplication = async (data, type = ''): Promise<boolean> => {
    setLoading(true);

    const calculatedApplicationFee = getCalculatedApplicationFee(data);
    const apiData = {
      ...data,
      IsSpayedNeutered: toBoolean(data.IsSpayedNeutered),
      ApplicationDate: data.ApplicationDate || new Date().toISOString().split('T')[0],
      LateFeesWaived: data.LateFeesWaived || false,
      ApplicationFee: calculatedApplicationFee
    };

    apiData.ApplicationType = applicationDetails?.ApplicationType;

    // Convert empty strings to null for optional date/email fields
    // RabiesExpirationDate is required - do not convert to null
    if (!data.RabiesIssueDate || data.RabiesIssueDate.trim() === '') {
      apiData.RabiesIssueDate = null;
    }
    // Keep OwnerEmailAddress as empty string instead of null to avoid database constraint issues
    if (!data.OwnerEmailAddress || data.OwnerEmailAddress.trim() === '') {
      apiData.OwnerEmailAddress = '';
    }
    if (!data.VetClinic || data.VetClinic.trim() === '') {
      apiData.VetClinic = null;
    }

    // Remove optional fields if empty
    if (!data.VetPhoneNumber || data.VetPhoneNumber.trim() === '') {
      delete apiData.VetPhoneNumber;
    }

    // Update the application with the form data
    const response = await httpRequest(
      'PATCH',
      `/pet/pet_application/update/${editData?.ApplicationID}`,
      apiData
    );

    if (response?.status === 'success') {
      // If type is provided, return true
      if (type) return true;

      toast.success(`Application update successfully.`);
      setLoading(false);

      // Navigate to appropriate page based on application status
      if (editData?.ApplicationStatus === 'Draft')
        navigate(
          `/${townshipId}/Department of Health or Animal Control/${permit}/draft-application`
        );
      else
        navigate(
          `/${townshipId}/Department of Health or Animal Control/${permit}/application-list`
        );

      return true;
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
      return false;
    }
  };

  /**
   * Handles approving an application
   * @param data - The form data from the UI (with IsSpayedNeutered as string)
   * @param approvalNote - Optional approval note
   */
  const handleApproveApplication = async (data, approvalNote?: string) => {
    setLoading(true);

    /**
     * Permit expires 01/01 of the next calendar year (after approval year).
     * Use literal calendar string — avoids local-midnight Jan 1 showing as 12/31 in some zones.
     */
    function getPermitExpiration(): string {
      const y = new Date().getFullYear();
      return `01/01/${y + 1}`;
    }

    // Prepare payload for updating application status
    // Convert IsSpayedNeutered from string to boolean for API
    const payload: any = {
      ApplicationID: Number(editData?.ApplicationID),
      ApplicationStatus: 'Approved',
      PermitExpiration: getPermitExpiration(),
      RabiesExpirationDate: data.RabiesExpirationDate || null, // Convert empty string to null
      RabiesIssueDate: data.RabiesIssueDate || null, // Convert empty string to null
      OwnerName: data.OwnerName,
      PetType: data.PetType,
      IsSpayedNeutered: toBoolean(data.IsSpayedNeutered), // Convert to boolean
      PetSex: data.PetSex,
      ApplicantName: data.ApplicantName,
      PetName: data.PetName,
      OwnerEmailAddress: data.OwnerEmailAddress || '', // Keep as empty string to avoid database constraint issues
      OwnerAddress: data.OwnerAddress
    };

    // Include ApprovalNote if provided
    if (approvalNote && approvalNote.trim()) {
      payload.ApprovalNote = approvalNote.trim();
    }

    // Update application status to approved
    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );

    if (response?.status === 'success') {
      // Send notification to the user
      sendNotification(socket, {
        UserID: editData?.UserID,
        TownshipID: townshipId,
        Title: 'Application Approved',
        Message: `Pet license application (${editData?.ApplicationNumber}) is approved.`,
        ApplicationNumber: editData?.ApplicationNumber
      });

      toast?.success(`Application Approved successfully.`);

      // Navigate to application list
      navigate(
        `/${townshipId}/Department of Health or Animal Control/${permit}/application-list`
      );
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }

    setLoading(false);
  };

  /**
   * Handles rejecting an application
   */
  const handleRejectApplication = async () => {
    setLoading(true);

    // Prepare payload for updating application status to rejected
    const payload = {
      ApplicationID: Number(editData?.ApplicationID),
      ApplicationStatus: 'Rejected',
      ReasonOfRejection: rejectReasonRef.current.trim()
    };

    // Update application status to rejected
    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );

    if (response?.status === 'success') {
      // Send notification to the user
      sendNotification(socket, {
        UserID: editData?.UserID,
        TownshipID: townshipId,
        Title: 'Application Rejected',
        Message: `Pet license application (${editData?.ApplicationNumber}) is rejected. ${rejectReasonRef.current.trim()}`,
        ApplicationNumber: editData?.ApplicationNumber
      });

      toast?.success(`Application Rejected successfully.`);
      setRejectModalOpen(false);
      rejectReasonRef.current = '';

      // Navigate to application list
      navigate(
        `/${townshipId}/Department of Health or Animal Control/${permit}/application-list`
      );
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }

    setLoading(false);
  };

  /**
   * Handles submitting an application (updates and changes status to In Review)
   * @param data - The form data to be submitted (may include pet details)
   */
  const handleSubmitApplication = async (data: any) => {
    setLoading(true);

    const calculatedApplicationFee = getCalculatedApplicationFee(data);
    const apiData = {
      ...data,
      ApplicationStatus: 'In Review',
      IsSpayedNeutered:
        typeof data.IsSpayedNeutered === 'string'
          ? toBoolean(data.IsSpayedNeutered)
          : data.IsSpayedNeutered,
      ApplicationDate: data.ApplicationDate || new Date().toISOString().split('T')[0],
      LateFeesWaived: data.LateFeesWaived || false,
      ApplicationFee: calculatedApplicationFee
    };

    apiData.ApplicationType = applicationDetails?.ApplicationType;

    // Remove optional fields if empty
    if (!data.RabiesIssueDate) {
      delete apiData.RabiesIssueDate;
    }

    if (!data.VetPhoneNumber) {
      delete apiData.VetPhoneNumber;
    }

    // First, update the application with the form data
    const updateResponse = await httpRequest(
      'PATCH',
      `/pet/pet_application/update/${editData?.ApplicationID}`,
      apiData
    );

    if (updateResponse?.status === 'success') {
        setLoading(false);
        toast.success('Application submitted successfully.');
        navigate(
          `/${townshipId}/Department of Health or Animal Control/${permit}/application-list`
        );
    } else {
      setLoading(false);
      toast.error(updateResponse?.message);
      dispatch(handleError(updateResponse?.message));
    }
  };

  /**
   * Handles form submission based on button clicked
   * @param data - The form data to be submitted (with IsSpayedNeutered as string)
   */
  const handleSubmit = async (data: PetApplicationFormDisplayData) => {
    // No need to convert IsSpayedNeutered here since we do it in each handler
const combinedAddress = combineOwnerAddress(
      (data as any).OwnerAddressLine1,
      (data as any).OwnerCity,
      (data as any).OwnerState,
      (data as any).OwnerZipCode
    );
    const {
      OwnerAddressLine1,
      OwnerCity,
      OwnerState,
      OwnerZipCode,
      ...dataWithoutSubAddressFields
    } = data as any;
    const payload = {
      ...dataWithoutSubAddressFields,
      OwnerAddress: combinedAddress,
      PetType: petDetails?.PetType ?? editData?.PetType,
      PetName: petDetails?.PetName ?? editData?.PetName,
      PetDOB: petDetails?.PetDOB ?? editData?.PetDOB ?? null,
      PetBreed: petDetails?.PetBreed ?? editData?.PetBreed,
      PetColor: petDetails?.PetColor ?? editData?.PetColor,
      PetHair: petDetails?.PetHair ?? editData?.PetHair,
      PetWeight: petDetails?.PetWeight ?? editData?.PetWeight,
      ChipNumber: petDetails?.ChipNumber ?? editData?.ChipNumber,
      PetSex: petDetails?.PetSex ?? editData?.PetSex,
      IsSpayedNeutered: petDetails?.IsSpayedNeutered ?? editData?.IsSpayedNeutered,
      PetID: petDetails?.PetID ?? editData?.PetID
    };

    // Handle form submission based on button clicked
    if (clickedOn === 'Proceed to Payment') {
      handleProceedToPay(payload);
    } else if (clickedOn === 'Update') {
      handleUpdateApplication(payload);
    } else if (clickedOn === 'Submit') {
      handleSubmitApplication(payload);
    } else if (clickedOn === 'Approve') {
      // Open approve modal first (don't update form yet)
      pendingApproveData.current = payload;
      setApproveModalOpen(true);
    } else if (clickedOn === 'Reject') {
      // Open reject modal to get rejection reason (don't update form first)
      setRejectModalOpen(true);
    }
  };

  /**
   * Determines which buttons to display based on application status
   * @returns string[] - Array of button names
   */
  const getFormButtons = (): string[] => {
    let buttonsCollection = ['Cancel'];

    if (editData?.ApplicationStatus) {
      // Show Update button for In Review or Draft status
      if (
        editData?.ApplicationStatus === 'In Review' ||
        editData?.ApplicationStatus === 'Draft'
      ) {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }

      // Show Proceed to Payment button for Draft status
      if (editData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Submit'];
      }

      // Show Approve and Reject buttons for managers/admins when application is In Review
      if (isManagerOrAdmin && editData?.ApplicationStatus === 'In Review') {
        buttonsCollection = [...buttonsCollection, 'Approve', 'Reject'];
      }
    }

    return buttonsCollection;
  };

  // Handle tax rate change
  const setTaxRate = useCallback((taxRate: number) => {
    setFormState((prev) => ({ ...prev, taxRate }));
  }, []);

  // Handle fee details change
  const setFeeDetails = useCallback((feeDetails: FeeDetails) => {
    setFormState((prev) => ({ ...prev, feeDetails }));
  }, []);

  return loading || !applicationDetails?.ApplicantName ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Pet License'} PageTitle={'Edit  Application'} />

      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={'edit'}
        applicationStatus={editData?.ApplicationStatus}
      />

      {switchTo ? (
        <Content
          switchTo={switchTo}
          setSwitchTo={setSwitchTo}
          applicationID={editData?.ApplicationID}
          UserID={userData?.UserID}
          ApplicationStatus={editData?.ApplicationStatus}
          ApplicationNumber={editData?.ApplicationNumber}
          selectMultipleFiles={true}
        />
      ) : (
        <Card>
          <Box
            m={{ md: 3, xs: 1 }}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 2 }
            }}
          >
            {editData && editData?.ApplicationStatus !== 'Draft' ? (
              <ApplicationStatus editAppStatus={editData} />
            ) : (
              <Typography variant="h4">Edit Application</Typography>
            )}

            <DownloadPopUp
              title={'PET LICENSE APPLICATION'}
              ApplicationDetails={{
                ApplicantName: applicationDetails?.ApplicantName,
                OwnerName: applicationDetails?.OwnerName,
                OwnerContactNumber: applicationDetails?.OwnerContactNumber,
                OwnerEmailAddress: applicationDetails?.OwnerEmailAddress,
                OwnerAddress: applicationDetails?.OwnerAddress,
                PetType: petDetails?.PetType,
                PetName: petDetails?.PetName,
                PetBreed: petDetails?.PetBreed,
                PetDateOfBirth: petDetails?.PetDOB,
                PetWeight: petDetails?.PetWeight,
                PetHair: petDetails?.PetHair,
                PetColor: petDetails?.PetColor,
                PetSex: petDetails?.PetSex,
                IsSpayedNeutered: toYesNo(petDetails?.IsSpayedNeutered),
                ChipNumber: petDetails?.ChipNumber,
                RabiesIssueDate: applicationDetails?.RabiesIssueDate,
                RabiesExpirationDate: applicationDetails?.RabiesExpirationDate,
                VetClinic: applicationDetails?.VetClinic,
                VetPhoneNumber: applicationDetails?.VetPhoneNumber
              }}
            />
          </Box>

          <ApplicationFormLayout
            sidebar={
              <PetSelection
                petDetails={petDetails}
                setPetDetails={setPetDetails}
                editData={editData}
              />
            }
            form={
              <FormikForm
                allFields={allFields}
                innerRef={formRef}
                buttonPlacement={'right'}
                defaultEnabledButton={['Proceed to Payment', 'Approve', 'Reject', 'Update', 'Submit']}
                buttonTitle={
                  editData
                    ? getFormButtons()
                    : ['Cancel', 'Save Draft', 'Proceed to Payment']
                }
                setClickedOn={setClickedOn}
                clickProp
                isNotDisable={true}
                content={
                  <>
                    <Box display={'flex'} gap={2} flexWrap="wrap">
                      <PetApplicationFeeWithFormik
                        setTaxRate={setTaxRate}
                        setFeeDetails={setFeeDetails}
                        feeDetails={formState.feeDetails}
                        isSpayedNeutered={
                          petDetails?.IsSpayedNeutered ??
                          editData?.IsSpayedNeutered
                        }
                      />
                    </Box>
                    {editData && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Content
                          switchTo={switchTo}
                          setSwitchTo={setSwitchTo}
                          applicationID={editData?.ApplicationID}
                          UserID={userData?.UserID}
                          ApplicationStatus={editData?.ApplicationStatus}
                          ApplicationNumber={editData?.ApplicationNumber}
                          selectMultipleFiles={false}
                        />
                      </Box>
                    )}
                  </>
                }
                formMethod={handleSubmit}
                heading={'Department of Health or Animal Control'}
                handleOtherButton={() => {
                  navigate(`/dashboard/${permit}`);
                }}
              />
            }
          />
        </Card>
      )}
      
      {/* Reject Application Modal */}
      <CustomModal
        title={'Reject Application?'}
        IsOpen={rejectModalOpen}
        setIsOpen={setRejectModalOpen}
        messageBodyRef={rejectReasonRef}
        loading={loading}
        onSave={() => {
          if (
            !rejectReasonRef.current ||
            (rejectReasonRef.current.trim() === '' ||
            (rejectReasonRef.current[0] === ' ' &&
              rejectReasonRef.current.length === 1))
          ) {
            toast.error("Reason field can't be empty.");
          } else {
            // Reject the application
            handleRejectApplication();
          }
        }}
        actionButtons={['Close', 'Confirm']}
        content={
          <TextField
            required
            className="txtArea"
            label="Reason"
            placeholder="Reason"
            name="Reason"
            multiline
            onChange={(e) => {
              rejectReasonRef.current = e.target.value;
            }}
            sx={{ width: '600px' }}
            rows={3}
          />
        }
      />
      
      {/* Approve Application Modal */}
      <CustomModal
        title={'Approve Application?'}
        IsOpen={approveModalOpen}
        setIsOpen={(open) => {
          setApproveModalOpen(open);
          if (!open) {
            // Reset when closing
            approvalNoteRef.current = '';
            pendingApproveData.current = null;
          }
        }}
        messageBodyRef={approvalNoteRef}
        loading={loading}
        onSave={async () => {
          // Submit with note (if provided) - called by "Submit" button
          if (pendingApproveData.current) {
            await handleApproveApplication(
              pendingApproveData.current,
              approvalNoteRef.current?.trim() || undefined
            );
            setApproveModalOpen(false);
            approvalNoteRef.current = '';
            pendingApproveData.current = null;
          }
        }}
        onSkip={async () => {
          // Skip & Submit - approve without note
          if (pendingApproveData.current) {
            await handleApproveApplication(
              pendingApproveData.current,
              undefined
            );
            setApproveModalOpen(false);
            approvalNoteRef.current = '';
            pendingApproveData.current = null;
          }
        }}
        actionButtons={['Skip & Submit', 'Submit']}
        content={
          <TextField
            className="txtArea"
            label="Approval Note (Optional)"
            placeholder="Add a note about this approval..."
            name="ApprovalNote"
            multiline
            onChange={(e) => {
              approvalNoteRef.current = e.target.value;
            }}
            sx={{ width: '600px' }}
            rows={3}
          />
        }
      />
    </Container>
  );
};

export default EditApplication;
